import {
  Box,
  Button,
  Dropdown,
  DropdownItem,
  InputField,
  Tabs,
  Tree,
} from "fds";
import { useTranslation } from "react-i18next";
import { getWorkspaceCode } from "../../shared/utils/auth";

interface Props {
  leftTab: number;
  setLeftTab: (v: number) => void;
  userStatus: string;
  setUserStatus: (v: string) => void;
  searchWord: string;
  tempSearchWord: string;
  setTempSearchWord: (v: string) => void;
  expandValues: string[];
  setExpandValues: (v: string[]) => void;
  targetData: { id: string };
  adminDeptStore: any;
  /** loginDto.ecoSystemLicenseStats.liteExternalLicenseCount */
  liteExternalLicenseCount: number;
  companyName: string;
  ROOT_DEPT_CODE: string;
  buttonClick: number;
  changeButtonClick: (v: number) => void;
  selectNode: (value: any) => void;
  doSearchWord: () => void;
  searchTree: (value: string) => void;
}

const DeptTreePanel: React.FC<Props> = ({
  leftTab,
  setLeftTab,
  userStatus,
  setUserStatus,
  searchWord,
  tempSearchWord,
  setTempSearchWord,
  expandValues,
  setExpandValues,
  targetData,
  adminDeptStore,
  liteExternalLicenseCount,
  companyName,
  ROOT_DEPT_CODE,
  buttonClick,
  changeButtonClick,
  selectNode,
  doSearchWord,
  searchTree,
}) => {
  const { t } = useTranslation();

  const userStatusLabel =
    userStatus === "ACTIVE"
      ? t("admin.dept.loginUser")
      : userStatus === "INACTIVE"
      ? t("admin.dept.inactiveUser")
      : userStatus === "LITE"
      ? t("admin.dept.otpUser")
      : t("admin.dept.allUser");

  const handleExpandToggle = (value: string, withSearch = false) => {
    if (expandValues.includes(value)) {
      setExpandValues(expandValues.filter((item) => item !== value));
    } else {
      setExpandValues([...expandValues, value]);
      if (withSearch) searchTree(value);
    }
  };

  const commonTreeProps = {
    depth: 0,
    checkType: "none" as const,
    treeType: "eco" as const,
    checkValues: [targetData.id],
    checkAction: () => {},
    expandValues,
    expandSearchValues: [targetData.id],
  };

  return (
    <Box direction="column" classes="adminDept-body-tree">
      {/* 탭: 내부/외부 사용자 */}
      <Box classes="adminDept-body-tab">
        <Tabs
          tabs={[
            {
              color: "default",
              isNew: false,
              text: t("admin.dept.internalUser"),
              isHidden:
                window.eco.serviceType === "OCI" &&
                getWorkspaceCode() === "MTECOCOMMON00001",
            },
            {
              color: "default",
              isNew: false,
              text: t("admin.dept.externalUser"),
            },
          ]}
          selectTab={setLeftTab}
          selected={leftTab}
          size="md"
          type="full"
          textAlign="center"
        />
      </Box>

      {/* 검색창 */}
      <Box direction="column">
        <Box direction="row" classes="mg mt-12">
          <InputField
            placeholder={t("admin.dept.searchInput")}
            size="sm"
            width={291}
            value={tempSearchWord}
            onChange={(e) => setTempSearchWord(e.target.value)}
            onKeyUp={(e) => {
              if (e.code === "Enter") doSearchWord();
            }}
          />
        </Box>

        {/* 필터 드롭다운 + 검색 버튼 */}
        <Box classes="mg mt-8">
          <Dropdown
            btnSize="sm"
            placeholder=" "
            value={userStatusLabel}
            width={214}
            buttonClick={buttonClick}
            disabled={searchWord !== ""}
          >
            <DropdownItem
              label={t("admin.dept.allUser")}
              onClick={() => {
                setUserStatus("ALL");
                changeButtonClick(buttonClick + 1);
                if (leftTab === 0) {
                  selectNode({
                    value: ROOT_DEPT_CODE,
                    columnText: companyName,
                    type: "company",
                  });
                } else {
                  selectNode({
                    value: "EXTERNAL",
                    columnText: t("admin.dept.externalUser"),
                    type: "company",
                  });
                }
                setExpandValues([ROOT_DEPT_CODE]);
              }}
            />
            <DropdownItem
              label={t("admin.dept.loginUser")}
              onClick={() => {
                setUserStatus("ACTIVE");
                changeButtonClick(buttonClick + 1);
                if (leftTab === 0) {
                  selectNode({
                    value: "INTERNAL_ACTIVE",
                    columnText: t("admin.dept.internalUser"),
                    type: "internal_active",
                  });
                } else {
                  selectNode({
                    value: "EXTERNAL_ACTIVE",
                    columnText: t("admin.dept.externalUser"),
                    type: "external_active",
                  });
                }
                setExpandValues([ROOT_DEPT_CODE]);
              }}
            />
            <DropdownItem
              label={t("admin.dept.otpUser")}
              isHidden={leftTab === 0 || liteExternalLicenseCount === 0}
              onClick={() => {
                changeButtonClick(buttonClick + 1);
                selectNode({
                  value: "EXTERNAL_AD_HOC",
                  columnText: t("admin.dept.externalUser"),
                  type: "external_lite",
                });
                setUserStatus("LITE");
                setExpandValues([ROOT_DEPT_CODE]);
              }}
            />
            <DropdownItem
              label={t("admin.dept.noRegisterUser")}
              isHidden={leftTab === 0}
              onClick={() => {
                changeButtonClick(buttonClick + 1);
                selectNode({
                  value: "EXTERNAL_INACTIVE",
                  columnText: t("admin.dept.externalUser"),
                  type: "external_inactive",
                });
                setUserStatus("INACTIVE");
                setExpandValues([ROOT_DEPT_CODE]);
              }}
            />
          </Dropdown>
          <Button
            label={t("admin.common.button.search")}
            size="sm"
            width={68}
            type="primary"
            btnStyle="fill"
            classes="mg ml-8"
            onClick={doSearchWord}
          />
        </Box>
      </Box>

      {/* ── 내부 사용자: 전체 (트리) ── */}
      {leftTab === 0 && userStatus === "ALL" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: companyName,
              subTree: [...adminDeptStore.internalTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value, true)}
            selectNode={selectNode}
          />
        </Box>
      )}

      {/* ── 내부 사용자: 활성 ── */}
      {leftTab === 0 && userStatus === "ACTIVE" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.internalUser"),
              subTree: [...adminDeptStore.internalActiveTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={selectNode}
          />
        </Box>
      )}

      {/* ── 내부 사용자: 검색 결과 ── */}
      {leftTab === 0 && searchWord !== "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.internalUser"),
              subTree: [...adminDeptStore.internalSearchTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                doSearchWord();
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}

      {/* ── 외부 사용자: 전체 ── */}
      {leftTab === 1 && userStatus === "ALL" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.externalUser"),
              subTree: [...adminDeptStore.externalTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                selectNode({
                  value: "EXTERNAL",
                  columnText: t("admin.dept.externalUser"),
                  type: "company",
                });
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}

      {/* ── 외부 사용자: Lite (OTP) ── */}
      {leftTab === 1 && userStatus === "LITE" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.externalUser"),
              subTree: [...adminDeptStore.externalLiteTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                selectNode({
                  value: "EXTERNAL_AD_HOC",
                  columnText: t("admin.dept.externalUser"),
                  type: "external_lite",
                });
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}

      {/* ── 외부 사용자: 활성 ── */}
      {leftTab === 1 && userStatus === "ACTIVE" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.externalUser"),
              subTree: [...adminDeptStore.externalActiveTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                selectNode({
                  value: "EXTERNAL_ACTIVE",
                  columnText: t("admin.dept.externalUser"),
                  type: "external_active",
                });
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}

      {/* ── 외부 사용자: 비활성 (미가입) ── */}
      {leftTab === 1 && userStatus === "INACTIVE" && searchWord === "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.externalUser"),
              subTree: [...adminDeptStore.externalInActiveTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                selectNode({
                  value: "EXTERNAL_INACTIVE",
                  columnText: t("admin.dept.externalUser"),
                  type: "external_inactive",
                });
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}

      {/* ── 외부 사용자: 검색 결과 ── */}
      {leftTab === 1 && searchWord !== "" && (
        <Box classes="adminDept-body-tree-dept">
          <Tree
            {...commonTreeProps}
            width="100%"
            treeData={{
              columnText: t("admin.dept.externalUser"),
              subTree: [...adminDeptStore.externalSearchTree.subTree],
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => handleExpandToggle(value)}
            selectNode={(value: any) => {
              if (value.value === ROOT_DEPT_CODE) {
                doSearchWord();
                setExpandValues([ROOT_DEPT_CODE]);
              } else {
                selectNode(value);
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DeptTreePanel;
